// Typed client for the gazar content API (api.gazar.dev). Runs in route loaders
// on the server (SSR) and on the client during navigation. The base URL comes
// from env: API_URL server-side, VITE_API_URL in the browser, defaulting to the
// production API so a missing env still works.
import { visitorId } from "../utils/visitor";

const DEFAULT_API = "https://api.gazar.dev";

export function apiBase(): string {
  if (typeof window === "undefined") {
    return process.env.API_URL || process.env.VITE_API_URL || DEFAULT_API;
  }
  return import.meta.env.VITE_API_URL || DEFAULT_API;
}

// Public email capture → the owned WaitlistEntry table (viewable/exportable in
// the admin). `source` tags where the signup came from (e.g. "newsletter").
export async function joinWaitlist(email: string, source: string): Promise<void> {
  const res = await fetch(`${apiBase()}/api/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, source }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
}

export interface ScholarshipApplicationInput {
  course?: string;
  name: string;
  email: string;
  role: string;
  company?: string;
  link?: string;
  building: string;
  why: string;
  commit: boolean;
  /** Honeypot. Always sent, always empty when a person filled the form. */
  website?: string;
}

// Public scholarship-seat application → the owned ScholarshipApplication table
// (reviewed/exported in the admin). Idempotent on (email, course) server-side.
export async function applyForScholarship(input: ScholarshipApplicationInput): Promise<void> {
  const res = await fetch(`${apiBase()}/api/scholarship`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
}

async function get<T>(path: string, token?: string): Promise<T> {
  const res = await fetch(`${apiBase()}/api/content${path}`, {
    // On the client the httpOnly session cookie rides along (credentials), so
    // gated content unlocks during client-side navigation; during SSR the loader
    // passes the token it read off the request and forwards it as a bearer.
    credentials: "include",
    headers: { accept: "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    throw new Response(res.statusText, { status: res.status });
  }
  return res.json() as Promise<T>;
}

export interface ArticleListItem {
  id: string;
  title: string;
  slug: string;
  category: string;
  description: string | null;
  image: unknown;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  viewCount: number;
  likeCount: number;
}

export interface ReviewMeta {
  rating: number | null;
  subjectTitle: string | null;
  subjectAuthor: string | null;
  subjectUrl: string | null;
  buyUrl: string | null;
}

// Optional hero video for an article. Stored polymorphically like `image`; a
// single progressive-download source (mp4 in the public assets bucket). When
// present, the article page renders the branded player instead of the cover.
export interface ArticleVideo {
  url: string;
  poster?: string | null;
  aspectRatio?: string | null;
}

export interface ArticleDetail extends ArticleListItem {
  body: string;
  content: string;
  video?: ArticleVideo | null;
  review?: ReviewMeta | null;
  related: { title: string; slug: string; category: string; description: string | null; image?: unknown }[];
}

export interface ArticleList {
  items: ArticleListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CourseLessonRef {
  slug: string;
  title: string;
  order: number;
  isFree: boolean;
  kind?: string; // "lesson" | "cheatsheet" | "lab" | "project"
  /**
   * Whether a recording exists for this lesson.
   *
   * Not the key, and never the URL: where a paid lesson's video is stored is not
   * public, but whether one exists is something a buyer is entitled to know
   * before paying rather than after.
   *
   * Optional because an older backend does not send it, and a missing field must
   * read as "unknown" rather than as "no recording", which would have every
   * lesson in every course claim it has none.
   */
  hasVideo?: boolean;
}

export interface CourseModuleRef {
  slug: string;
  title: string;
  order: number;
  lessons: CourseLessonRef[];
}

export interface CourseDetail {
  id: string;
  title: string;
  slug: string;
  price: number;
  level: string | null;
  shortDescription: string | null;
  introBody: string | null;
  intro: string | null;
  image: { url?: string } | null;
  paid: boolean;
  productSlug: string;
  publishedAt: string;
  /** Last content change. What the "Updated" pill shows, since publishedAt is hand-set. */
  updatedAt?: string;
  modules: CourseModuleRef[];
  /**
   * How much of the course is recorded. Absent from an older backend.
   *
   * Two of the course intros say "The recordings teach; the cohort reviews your
   * work", one of them on a $299 page. Until this existed nothing on the site
   * could say how many lessons that was, and the only way to find out was to buy.
   */
  videoCoverage?: { recorded: number; total: number };
  // Social proof: published testimonials tagged to this course. Empty when none.
  testimonials?: CourseTestimonial[];
  // Course intro/trailer. Always public (`free/` prefix), so `requiresToken` is
  // false. null/absent when the course has no trailer.
  video?: {
    src: string;
    requiresToken: boolean;
    courseSlug: string;
    duration: number | null;
  } | null;
}

export interface CourseTestimonial {
  id: string;
  name: string;
  title: string | null;
  body: string;
  slug: string;
  publishedAt: string;
}

export interface LessonDetail {
  id: string;
  title: string;
  slug: string;
  isFree: boolean;
  kind?: string; // "lesson" | "cheatsheet" | "lab" | "project"
  order: number;
  module: { slug: string; title: string; courseId: string };
  locked: boolean;
  price?: number;
  productSlug?: string;
  body: string | null;
  content: string | null;
  // Present (non-null) only on an accessible lesson that has a video. `requiresToken`
  // is true for gated (`hls/`) sources that need a playback token first; false for
  // public (`free/`) sources. Absent/null on locked or markdown-only lessons.
  video?: {
    src: string;
    requiresToken: boolean;
    courseSlug: string;
    duration: number | null;
  } | null;
}

// Record that this browser read an article. Fire-and-forget: a failed counter
// must never surface to a reader, so this swallows everything. Server-side it's
// a no-op — see utils/visitor.ts for why views are browser-only.
export async function trackView(articleId: string): Promise<void> {
  const vid = visitorId();
  if (!vid) return;
  try {
    await fetch(`${apiBase()}/api/content/articles/${articleId}/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId: vid }),
      keepalive: true,
    });
  } catch {
    // Counting is best-effort. Never let it break the page.
  }
}

export interface PopularArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  description?: string | null;
  image?: unknown;
  views: number;
}

export interface MentorshipService {
  id: string;
  slug: string;
  title: string;
  duration: string | null;
  /** Display price, e.g. "$65", "FREE", "$200/month". */
  price: string | null;
  /** Whole currency units. What the booking flow actually charges. */
  priceValue: number;
  emoji: string | null;
  description: string | null;
  popular: boolean;
  isFree: boolean;
  requiresConfirmation: boolean;
  order: number;
}

export type BookingStatus =
  | "awaiting_payment"
  | "pending"
  | "paid"
  | "confirmed"
  | "completed"
  | "declined"
  | "cancelled"
  | "expired";

export interface Availability {
  service: { slug: string; title: string; duration: string | null; minutes: number };
  horizonDays: number;
  /** Bookable start instants, ISO. Slot ids stay server-side. */
  slots: { startsAt: string }[];
}

export interface Booking {
  reference: string;
  serviceSlug: string;
  serviceTitle: string;
  duration: string | null;
  /** Minor units (cents). Snapshotted when the request was made. */
  amount: number;
  currency: string;
  priceLabel: string | null;
  name: string;
  email: string;
  timezone: string;
  goal: string;
  status: BookingStatus;
  /** The slot they picked, ISO. */
  scheduledAt: string | null;
  minutes: number;
  meetingUrl: string | null;
  ownerNote: string | null;
  paidAt: string | null;
  refundedAt: string | null;
  createdAt: string;
  /** Slot held, money not in yet: the status page shows the Pay button. */
  awaitingPayment: boolean;
  /** Paid (or free) and waiting on Gazar to confirm. */
  awaitingOwner: boolean;
}

export interface BookingRequestInput {
  service: string;
  /** The published start instant they picked, ISO. */
  startsAt: string;
  name: string;
  email: string;
  timezone: string;
  company?: string;
  link?: string;
  goal: string;
}

// The booking endpoints live outside /api/content (they write, and they're not
// content), so they don't go through `get`. No auth: a session request is open to
// any reader, and the returned `reference` is the only key to the booking after.
async function bookingRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      accept: "application/json",
      // Declared only when there IS a body. The checkout and cancel calls are
      // bodiless POSTs (the reference in the path is the whole request), and
      // Fastify rejects those outright with "Body cannot be empty when
      // content-type is set to 'application/json'" — which surfaced as a red
      // error under the Pay button instead of a Stripe redirect.
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
  return res.json() as Promise<T>;
}

export const bookings = {
  /** Open slots for a session type. Also tops up the generated horizon. */
  availability: (service: string) =>
    bookingRequest<Availability>(`/availability?service=${encodeURIComponent(service)}`),
  /**
   * Take a slot. A paid session comes back with `checkoutUrl` and the slot held
   * for 20 minutes; a free one is booked outright and waits on Gazar.
   */
  create: (input: BookingRequestInput) =>
    bookingRequest<Booking & { checkoutUrl?: string }>("/bookings", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  get: (reference: string) => bookingRequest<Booking>(`/bookings/${reference}`),
  /** Mint a fresh Stripe Checkout session (re-taking the slot if the hold lapsed). */
  checkout: (reference: string) =>
    bookingRequest<{ url: string }>(`/bookings/${reference}/checkout`, { method: "POST" }),
  cancel: (reference: string) =>
    bookingRequest<Booking>(`/bookings/${reference}/cancel`, { method: "POST" }),
};

/** One back issue as the archive index lists it. */
export interface NewsletterIssueSummary {
  publicSlug: string;
  subject: string;
  sentAt: string;
}

/** One back issue, in full. `body` is the paragraphs as they were emailed. */
export interface NewsletterIssue extends NewsletterIssueSummary {
  body: string[];
}

export const content = {
  /** Most-read articles in a rolling window. Empty until people actually read. */
  popular: (days = 7, limit = 6) => get<PopularArticle[]>(`/articles/popular?days=${days}&limit=${limit}`),
  articles: (params: { category?: string; q?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.category) qs.set("category", params.category);
    if (params.q) qs.set("q", params.q);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return get<ArticleList>(`/articles${suffix}`);
  },
  article: (category: string, slug: string) => get<ArticleDetail>(`/articles/${category}/${slug}`),
  /** Back issues on the public archive. Only ones actually sent and deliberately published. */
  newsletterIssues: () => get<{ items: NewsletterIssueSummary[]; total: number }>("/newsletter"),
  newsletterIssue: (slug: string) => get<NewsletterIssue>(`/newsletter/${slug}`),
  clusters: () => get<any[]>("/clusters"),
  courses: () => get<any[]>("/courses"),
  course: (slug: string) => get<CourseDetail>(`/courses/${slug}`),
  // Pass the caller's token so the server can unlock paid lessons for an entitled
  // user; without it, only free courses / free-preview lessons return a body.
  lesson: (slug: string, lessonSlug: string, token?: string) =>
    get<LessonDetail>(`/courses/${slug}/lessons/${lessonSlug}`, token),
  podcasts: () => get<any[]>("/podcasts"),
  podcast: (slug: string) => get<any>(`/podcasts/${slug}`),
  projects: () => get<any[]>("/projects"),
  testimonials: () => get<any[]>("/testimonials"),
  mentorship: () => get<MentorshipService[]>("/mentorship"),
  mentorshipService: (slug: string) => get<MentorshipService>(`/mentorship/${slug}`),
  maven: () => get<any[]>("/maven"),
  talks: () => get<any[]>("/talks"),
  jobs: () => get<any[]>("/jobs"),
  single: (slug: string) => get<any>(`/singles/${slug}`),
  settings: () => get<Record<string, string>>("/settings"),
};
